// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file PresenceContext.jsx
 * @description Provider global para gestión de presencia - Listeners WebSocket UNA SOLA VEZ
 * @version 1.0.0
 */

import { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import socketService from '../services/socketService';
import { useAuth } from './AuthContext';
import { updateUserPresence as updatePresenceService, getUserPresence } from '../services/presenceService';
import { loadPresencePresets } from '../services/presencePresetsClient';
import { dispatchPresenceUpdated, PRESENCE_UPDATED_EVENT } from '../services/presenceRealtimeEvents';

/** Evita doble bootstrap en React Strict / remount (refs se reinician). */
const __presenceBootstrapDone = new Set();
const __presenceBootstrapInFlight = new Map();
/** Una promoción offline→online tras login por sesión (evita doble PUT en StrictMode). */
const __presenceOnlineOnLoginDone = new Set();

const PresenceContext = createContext({
  presence: null,
  loading: false,
  error: null,
  updating: false,
  updatePresence: () => {}
  // loadPresence NO se expone - se ejecuta automáticamente cuando es necesario
});

/**
 * Provider de Presence - Garantiza listeners WebSocket UNA SOLA VEZ
 */
export const PresenceProvider = ({ children }) => {
  console.log('👁️ [PRESENCE_PROVIDER] Renderizando PresenceProvider');
  const { user, isAuthenticated, updateUser } = useAuth();
  const [presence, setPresence] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updating, setUpdating] = useState(false);
  
  const lastLoadedUserIdRef = useRef(null);
  const isLoadingRef = useRef(false);
  const isUpdatingRef = useRef(false);
  const listenersRegisteredRef = useRef(false);
  const presenceUpdateHandlerRef = useRef(null);
  const initializedRef = useRef(false);

  // Log de inicialización UNA SOLA VEZ
  useEffect(() => {
    if (!initializedRef.current) {
      console.log('[PRESENCE_PROVIDER] INIT');
      initializedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    loadPresencePresets().catch(() => {});
  }, [isAuthenticated, user?.id]);

  /**
   * Carga el estado de presencia
   */
  const loadPresence = useCallback(async () => {
    if (!user?.id) return;

    if (__presenceBootstrapDone.has(user.id)) {
      console.log('[PRESENCE_FLOW][SKIP_DUPLICATE_FETCH]');
      return;
    }
    if (__presenceBootstrapInFlight.get(user.id)) {
      console.log('[PRESENCE_FLOW][SKIP_DUPLICATE_FETCH]');
      return;
    }
    
    // Evitar múltiples llamadas simultáneas para el mismo usuario
    if (isLoadingRef.current && lastLoadedUserIdRef.current === user.id) {
      return;
    }

    __presenceBootstrapInFlight.set(user.id, true);
    console.log('[PRESENCE_FLOW][BOOTSTRAP_ONCE]', { userId: user.id });

    isLoadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      console.log('[PRESENCE_PROVIDER] loadPresence ejecutado para usuario:', user.id);
      // Usar servicio centralizado
      const result = await getUserPresence(user.id);
      
      if (result.success && result.presence) {
        let normalizedPresence = {
          usuario_id: result.presence.usuario_id || user.id,
          status: result.presence.status || 'online',
          custom_status: result.presence.custom_status || null,
          custom_emoji: result.presence.custom_emoji || null,
          expires_at: result.presence.expires_at || null,
          preset_key: result.presence.preset_key != null ? result.presence.preset_key : null,
        };

        const needsOnlineAfterLogin =
          String(normalizedPresence.status || '').toLowerCase() === 'offline'
          && !__presenceOnlineOnLoginDone.has(user.id);
        if (needsOnlineAfterLogin) {
          __presenceOnlineOnLoginDone.add(user.id);
          const promote = await updatePresenceService(user.id, 'online');
          if (promote?.success && promote.presence) {
            normalizedPresence = promote.presence;
            if (typeof updateUser === 'function' && user) {
              updateUser({
                ...user,
                presence_status: normalizedPresence.status,
                status: normalizedPresence.status,
                custom_status: normalizedPresence.custom_status ?? null,
                custom_emoji: normalizedPresence.custom_emoji ?? null,
                preset_key: normalizedPresence.preset_key ?? null,
              });
            }
          }
        } else if (String(normalizedPresence.status || '').toLowerCase() === 'online') {
          __presenceOnlineOnLoginDone.add(user.id);
        }
        
        console.log('[PRESENCE_PROVIDER] ✅ Presencia cargada:', normalizedPresence);
        setPresence(normalizedPresence);
        lastLoadedUserIdRef.current = user.id;
        __presenceBootstrapDone.add(user.id);
      } else {
        // Degradación: GET presencia falla (p. ej. esquema incompleto) — no bloquear sesión; reflejar perfil u online hasta WS/API.
        const errorMsg = result.error || 'Error al cargar presencia';
        console.warn('[PRESENCE_PROVIDER] ⚠️ Presencia no disponible, modo degradado:', errorMsg);
        const fromProfile = String(user?.presence_status || user?.status || '').toLowerCase().trim();
        const allowed = ['online', 'idle', 'dnd', 'busy', 'offline', 'custom'];
        const st = allowed.includes(fromProfile) ? fromProfile : 'online';
        const normalizedPresence = {
          usuario_id: user.id,
          status: st,
          custom_status: user?.custom_status ?? null,
          custom_emoji: user?.custom_emoji ?? null,
          expires_at: user?.custom_status_expires_at ?? user?.expires_at ?? null,
          preset_key: user?.preset_key != null ? user.preset_key : null,
        };
        setPresence(normalizedPresence);
        setError(null);
        lastLoadedUserIdRef.current = user.id;
        __presenceBootstrapDone.add(user.id);
      }
    } catch (err) {
      const msg = err.message || 'Error de conexión';
      console.warn('[PRESENCE_PROVIDER] ⚠️ Excepción en loadPresence, modo degradado:', msg);
      try {
        const fromProfile = String(user?.presence_status || user?.status || '').toLowerCase().trim();
        const allowed = ['online', 'idle', 'dnd', 'busy', 'offline', 'custom'];
        const st = allowed.includes(fromProfile) ? fromProfile : 'online';
        setPresence({
          usuario_id: user.id,
          status: st,
          custom_status: user?.custom_status ?? null,
          custom_emoji: user?.custom_emoji ?? null,
          expires_at: user?.custom_status_expires_at ?? user?.expires_at ?? null,
          preset_key: user?.preset_key != null ? user.preset_key : null,
        });
        setError(null);
        lastLoadedUserIdRef.current = user.id;
        __presenceBootstrapDone.add(user.id);
      } catch {
        setError(msg);
      }
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      if (user?.id) __presenceBootstrapInFlight.delete(user.id);
    }
  }, [user, updateUser]);

  /**
   * Actualiza el estado de presencia
   * Usa el servicio centralizado updateUserPresence
   */
  const updatePresence = useCallback(async (status, customData = null) => {
    // 🔥 PRESENCE FLOW — Log obligatorio
    console.log('[PRESENCE FLOW] updatePresence llamado:', status, { customData: !!customData });
    
    if (!user?.id) {
      console.error('🔥 [PRESENCE CONTEXT] updatePresence: Usuario no identificado');
      return { success: false, error: 'Usuario no identificado' };
    }

    if (isUpdatingRef.current) {
      console.warn('🔥 [PRESENCE CONTEXT] updatePresence: Actualización en progreso, ignorando');
      return { success: false, error: 'Actualización en progreso' };
    }

    isUpdatingRef.current = true;
    setUpdating(true);
    setError(null);

    try {
      console.log('🔥 [PRESENCE CONTEXT] updatePresence: Llamando servicio centralizado', {
        usuarioId: user.id,
        status,
        hasCustomData: !!customData
      });

      // Usar servicio centralizado
      const result = await updatePresenceService(user.id, status, customData);
      
      // 🔥 PROTECCIÓN CONTRA UNDEFINED
      if (!result) {
        const errorMsg = 'No response from server';
        console.error('🔥 [PRESENCE CONTEXT] updatePresence: ❌ Result es undefined');
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }

      if (result.success && result.presence) {
        const normalizedPresence = result.presence;
        
        console.log('🔥 [PRESENCE CONTEXT] updatePresence: ✅ Actualización exitosa, actualizando estado local', {
          normalizedPresence
        });
        
        // 🔥 FIX DEFINITIVO: Usar directamente el valor retornado por el backend
        setPresence(normalizedPresence);

        // Sincronizar con AuthContext (respuesta PUT inmediata)
        if (typeof updateUser === 'function' && user) {
          updateUser({
            ...user,
            presence_status: normalizedPresence.status,
            status: normalizedPresence.status,
            custom_status: normalizedPresence.custom_status ?? user.custom_status,
            custom_emoji: normalizedPresence.custom_emoji ?? user.custom_emoji,
            preset_key: normalizedPresence.preset_key ?? user.preset_key,
          });
        }

        // Fuente de verdad: respuesta PUT + WS/eventos; sin GET redundante tras PUT.
        // Actualizar referencias para evitar recargas innecesarias
        isLoadingRef.current = false;
        lastLoadedUserIdRef.current = user.id; // Marcar como cargado para este usuario
        
        // El backend emite WebSocket; el listener puede refinar el estado
        
        console.log('🔥 [PRESENCE CONTEXT] updatePresence: ✅ Estado actualizado directamente desde respuesta backend');
        
        return { success: true, presence: normalizedPresence };
      } else {
        const errorMsg = result.error || 'Error al actualizar presencia';
        console.error('🔥 [PRESENCE CONTEXT] updatePresence: ❌ Error:', errorMsg);
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err) {
      const errorMsg = err.message || 'Error de conexión';
      console.error('🔥 [PRESENCE CONTEXT] updatePresence: ❌ Excepción:', errorMsg);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      isUpdatingRef.current = false;
      setUpdating(false);
    }
  }, [user, updateUser]); // user para updateUser; updateUser estable desde AuthContext

  // Cargar presencia cuando el usuario está autenticado
  // NO incluir loadPresence en dependencias - se ejecuta solo cuando cambia user.id
  useEffect(() => {
    if (isAuthenticated && user?.id && lastLoadedUserIdRef.current !== user.id) {
      loadPresence();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.id]); // loadPresence es estable (useCallback con user?.id)

  useEffect(() => {
    if (!isAuthenticated) {
      __presenceBootstrapDone.clear();
      __presenceBootstrapInFlight.clear();
      __presenceOnlineOnLoginDone.clear();
    }
  }, [isAuthenticated]);

  // Evento global `archat:presence_updated`: usuario actual (UI inmediata; PUT ya aplicó — skip source put)
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const onWindowPresence = (e) => {
      const d = e.detail;
      if (!d || d.user_id == null) return;
      const uid = parseInt(String(d.user_id), 10);
      const myId = parseInt(String(user.id), 10);
      if (!Number.isFinite(uid) || uid !== myId) return;
      if (d.source === 'put') return;

      const normalizedPresence = {
        usuario_id: uid,
        status: d.status || 'online',
        custom_status: d.custom_status ?? null,
        custom_emoji: d.custom_emoji ?? null,
        expires_at: d.expires_at ?? null,
        preset_key: d.preset_key != null ? d.preset_key : null,
      };
      setPresence(normalizedPresence);
      if (typeof updateUser === 'function' && user) {
        updateUser({
          ...user,
          presence_status: normalizedPresence.status,
          status: normalizedPresence.status,
          custom_status: normalizedPresence.custom_status ?? user.custom_status,
          custom_emoji: normalizedPresence.custom_emoji ?? user.custom_emoji,
          preset_key: normalizedPresence.preset_key ?? user.preset_key,
        });
      }
    };
    window.addEventListener(PRESENCE_UPDATED_EVENT, onWindowPresence);
    return () => window.removeEventListener(PRESENCE_UPDATED_EVENT, onWindowPresence);
  }, [isAuthenticated, user?.id, user, updateUser]);

  // Listeners WS: `socketService.on` encola hasta `io()`; cleanup obligatorio (StrictMode / desmontaje).
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      if (listenersRegisteredRef.current && presenceUpdateHandlerRef.current) {
        socketService.off('presence_updated', presenceUpdateHandlerRef.current);
        socketService.off('user_presence_changed', presenceUpdateHandlerRef.current);
        listenersRegisteredRef.current = false;
        presenceUpdateHandlerRef.current = null;
      }
      return;
    }

    const handlePresenceUpdate = (data) => {
      const uidRaw = data?.usuario_id ?? data?.presence?.usuario_id;
      if (uidRaw == null) return;
      const uid = parseInt(String(uidRaw), 10);
      if (!Number.isFinite(uid)) return;
      const p = data.presence || data;
      const status = p?.status || p?.presence_status || 'online';
      dispatchPresenceUpdated({
        user_id: uid,
        status,
        custom_status: p?.custom_status ?? null,
        custom_emoji: p?.custom_emoji ?? null,
        preset_key: p?.preset_key != null ? p.preset_key : null,
        expires_at: p?.expires_at ?? null,
        source: 'ws',
      });
    };

    socketService.on('presence_updated', handlePresenceUpdate);
    socketService.on('user_presence_changed', handlePresenceUpdate);
    listenersRegisteredRef.current = true;
    presenceUpdateHandlerRef.current = handlePresenceUpdate;

    return () => {
      socketService.off('presence_updated', handlePresenceUpdate);
      socketService.off('user_presence_changed', handlePresenceUpdate);
      listenersRegisteredRef.current = false;
      presenceUpdateHandlerRef.current = null;
    };
  }, [isAuthenticated, user?.id]);

  // NO exponer loadPresence - solo se usa internamente
  // Los componentes no deben llamar loadPresence manualmente
  const value = {
    presence,
    loading,
    error,
    updating,
    updatePresence
    // loadPresence NO se expone - se ejecuta automáticamente cuando es necesario
  };

  console.log('👁️ [PRESENCE_PROVIDER] Renderizando children');
  return (
    <PresenceContext.Provider value={value}>
      {children}
    </PresenceContext.Provider>
  );
};

/**
 * Hook para usar el contexto de Presence
 */
export const usePresence = (userId = null) => {
  const context = useContext(PresenceContext);
  if (!context) {
    throw new Error('usePresence debe usarse dentro de PresenceProvider');
  }
  return context;
};

export default PresenceContext;
